import {
  MapPin, Wind, Drop, Newspaper, ArrowSquareOut,
  GlobeHemisphereWest, Sun, UserCircle,
} from "@phosphor-icons/react";
import { Link } from "react-router-dom";

export const slugifyLoc = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

// Renders a photo's location (city, state, country) as links into the /photos hub.
// A city link needs a resolvable state (city URLs are 3 segments), so a city with no
// state is shown as plain text while state/country stay linked.
export function LocationLinks({ location, className = "" }) {
  const { city, state, country } = location || {};
  if (!city && !country) return null;
  const cSlug = slugifyLoc(country), sSlug = slugifyLoc(state), citySlug = slugifyLoc(city);
  const parts = [];
  if (city) parts.push(country && state
    ? { key: "city", label: city, to: `/photos/${cSlug}/${sSlug}/${citySlug}` }
    : { key: "city", label: city, to: null });
  if (state) parts.push({ key: "state", label: state, to: country ? `/photos/${cSlug}/${sSlug}` : null });
  if (country) parts.push({ key: "country", label: country, to: `/photos/${cSlug}` });
  return (
    <span className={className} data-testid="location-links">
      {parts.map((p, i) => (
        <span key={p.key}>
          {i > 0 && <span className="text-zinc-400">, </span>}
          {p.to
            ? <Link to={p.to} data-testid={`location-link-${p.key}`}
                className="font-semibold text-[#F27A54] underline decoration-[#F27A54]/40 decoration-dotted underline-offset-2 hover:decoration-solid">{p.label}</Link>
            : <span>{p.label}</span>}
        </span>
      ))}
    </span>
  );
}

export function WeatherCard({ weather }) {
  if (!weather || weather.temp_c === undefined || weather.temp_c === null) return null;
  return (
    <div data-testid="place-weather" className="rounded-2xl border border-border bg-card p-5">
      <div className="flex items-center gap-2 text-[#F27A54]">
        <Sun size={18} weight="fill" />
        <p className="overline text-[10px]">Weather now</p>
      </div>
      <div className="mt-2 flex items-end gap-3">
        <span className="font-display text-4xl font-black">{Math.round(weather.temp_c)}°</span>
        <span className="pb-1.5 text-sm font-semibold text-muted-foreground">{weather.description}</span>
      </div>
      <div className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-muted-foreground">
        {weather.humidity != null && <span className="flex items-center gap-1"><Drop size={13} weight="fill" /> {weather.humidity}% humidity</span>}
        {weather.wind_kmh != null && <span className="flex items-center gap-1"><Wind size={13} weight="fill" /> {Math.round(weather.wind_kmh)} km/h</span>}
      </div>
      {weather.forecast?.length > 0 && (
        <div className="mt-4 grid grid-cols-3 gap-2">
          {weather.forecast.map((d) => (
            <div key={d.date} className="rounded-lg bg-zinc-50 p-2 text-center">
              <p className="text-[10px] font-bold text-muted-foreground">
                {new Date(d.date).toLocaleDateString(undefined, { weekday: "short" })}
              </p>
              <p className="mt-1 text-xs font-black">{d.max_c != null ? Math.round(d.max_c) : "—"}°</p>
              <p className="text-[10px] text-muted-foreground">{d.min_c != null ? Math.round(d.min_c) : "—"}°</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function FactsCard({ place }) {
  const f = place.facts || {};
  const rows = [];
  if (place.country && place.type !== "country") rows.push(["Country", place.country]);
  if (f.continent) rows.push(["Continent", f.continent]);
  if (f.admin1) rows.push(["Region", f.admin1]);
  if (f.country_code) rows.push(["ISO code", f.country_code]);
  if (f.population) rows.push(["Population", Number(f.population).toLocaleString()]);
  if (f.elevation != null) rows.push(["Elevation", `${Math.round(f.elevation)} m`]);
  if (f.timezone) rows.push(["Timezone", f.timezone]);
  if (rows.length === 0) return null;
  return (
    <div data-testid="place-facts" className="rounded-2xl border border-border bg-card p-5">
      <div className="flex items-center gap-2 text-[#2D6A4F]">
        <GlobeHemisphereWest size={18} weight="fill" />
        <p className="overline text-[10px]">Key facts</p>
      </div>
      <dl className="mt-3 space-y-2">
        {rows.map(([k, v]) => (
          <div key={k} className="flex items-center justify-between gap-3 text-sm">
            <dt className="text-muted-foreground">{k}</dt>
            <dd className="font-semibold">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export function NewsList({ news }) {
  if (!news?.length) return null;
  return (
    <div data-testid="place-news" className="rounded-2xl border border-border bg-card p-5">
      <div className="flex items-center gap-2 text-[#F27A54]">
        <Newspaper size={18} weight="fill" />
        <p className="overline text-[10px]">Latest news</p>
      </div>
      <ul className="mt-3 space-y-3">
        {news.map((n, i) => (
          <li key={i}>
            <a href={n.link} target="_blank" rel="noreferrer" data-testid={`place-news-${i}`} className="group block">
              <p className="line-clamp-2 text-sm font-semibold leading-snug group-hover:text-[#F27A54]">{n.title}</p>
              <p className="mt-0.5 text-[11px] text-muted-foreground">
                {n.source ? `${n.source} · ` : ""}{n.published ? new Date(n.published).toLocaleDateString() : ""}
              </p>
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function RelatedLinks({ links }) {
  if (!links?.length) return null;
  return (
    <div data-testid="place-related" className="rounded-2xl border border-border bg-card p-5">
      <div className="flex items-center gap-2 text-[#7C3AED]">
        <ArrowSquareOut size={18} weight="fill" />
        <p className="overline text-[10px]">Related on the network</p>
      </div>
      <div className="mt-3 space-y-2">
        {links.map((l) => (
          <a key={l.site} href={l.url} target="_blank" rel="noreferrer" data-testid={`place-link-${l.site}`}
            className="flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-2 text-sm transition-colors hover:border-[#F27A54] hover:bg-orange-50/50">
            <span>
              <span className="font-bold">{l.label}</span>
              <span className="ml-2 text-[11px] text-muted-foreground">{l.blurb}</span>
            </span>
            <ArrowSquareOut size={14} className="shrink-0 text-muted-foreground" />
          </a>
        ))}
      </div>
    </div>
  );
}

export function FeaturedPhotographerCard({ photographer }) {
  if (!photographer?.name) return null;
  return (
    <div data-testid="place-photographer" className="rounded-2xl border border-border bg-card p-5">
      <div className="flex items-center gap-2 text-[#2D6A4F]">
        <UserCircle size={18} weight="fill" />
        <p className="overline text-[10px]">Featured photographer</p>
      </div>
      <Link to={photographer.url || "#"} data-testid="place-photographer-link"
        className="mt-3 flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-2.5 transition-colors hover:border-[#F27A54] hover:bg-orange-50/50">
        <span>
          <span className="block text-sm font-bold">{photographer.name}</span>
          <span className="flex items-center gap-1 text-[11px] text-muted-foreground">
            <MapPin size={11} weight="fill" /> {photographer.count} photo{photographer.count === 1 ? "" : "s"} here
          </span>
        </span>
        <ArrowSquareOut size={14} className="shrink-0 text-muted-foreground" />
      </Link>
    </div>
  );
}
